<script>
    // Import axios for API consuming in a clean interface.
    import axios from 'axios';

    // Import general mixin for shared functionality.
    import mixin from '../../../mixin';

    // Import filters
    import FilterClientName from '../../filters/ClientName.vue';
    import FilterClientId from '../../filters/ClientId.vue';
    import FilterCreditorName from '../../filters/CreditorName.vue';
    import FilterCreditorId from '../../filters/CreditorId.vue';
    import FilterWaitingStatus from '../../filters/WaitingStatus.vue';
    import FilterPeriod from '../../filters/Period.vue';
    import FilterPhase from '../../filters/Phase.vue';
    import FilterClosingReason from '../../filters/ClosingReason.vue';

    // Import widgets
    import WidgetText from '../../widgets/Text.vue';
    import WidgetAverageEfficiencyPerPeriod from '../../widgets/AverageEfficiencyPerPeriod.vue';
    import WidgetDossiersPerMonth from '../../widgets/DossiersPerMonth.vue';
    import WidgetDossiersInTreatment from '../../widgets/DossiersInTreatment.vue';
    import WidgetDossiersClosed from '../../widgets/DossiersClosed.vue';
    import WidgetDossiersSums from '../../widgets/DossiersSums.vue';
    import WidgetAverageEfficiencyPerMonth from '../../widgets/AverageEfficiencyPerMonth.vue';
    import WidgetDossiersPerStatusPerPeriod from '../../widgets/DossiersPerStatusPerPeriod.vue';

    export default {
        mixins: [mixin],
        components: {
            FilterClientName,
            FilterClientId,
            FilterCreditorName,
            FilterCreditorId,
            FilterWaitingStatus,
            FilterPeriod,
            FilterPhase,
            FilterClosingReason,
            WidgetText,
            WidgetAverageEfficiencyPerPeriod,
            WidgetDossiersPerMonth,
            WidgetDossiersInTreatment,
            WidgetDossiersClosed,
            WidgetDossiersSums,
            WidgetAverageEfficiencyPerMonth,
            WidgetDossiersPerStatusPerPeriod,
        },
        data() {
            return {
                api: this.$store.getters.getText('dashboardApiEndpointDossiersManagement'),
                loading: false,
                data: null,
                filters: [],
                facetAliasYear: 'ingangsjaar',
                facetAliasYearMonth: 'ingangsjaarmaand',
            };
        },
        computed: {
            facetsSet() {
                return this.data && this.data.facets && this.data.facets.length;
            },
            mostRecentYear() {
                if (!this.facetsSet) return null;
                const facet = this.data.facets.find(f => f.alias === this.facetAliasYear);
                if (!facet || !facet.options || !facet.options.length) return null;
                const index = facet.options.length - 1;
                return parseInt(facet.options.map(o => o.valueAlias)[index]);
            },
            secondRecentYear() {
                return this.mostRecentYear ? this.mostRecentYear - 1 : null;
            },
            periods() {
                if (!this.mostRecentYear) return [];
                return [this.$store.getters.getText('dashboardFilterPeriodOlder'), this.secondRecentYear, this.mostRecentYear];
            },
            firstAndLastDate() {
                const defaultValue = {
                    firstDate: { year: 0, month: 0 },
                    lastDate: { year: 0, month: 0 },
                };
                if (!this.facetsSet) return defaultValue;
                const facet = this.data.facets.find(f => f.alias === this.facetAliasYearMonth);
                if (!facet || !facet.options || !facet.options.length) return defaultValue;
                const firstValue = this.sortYearMonth(facet.options.filter(o => o.filteredAmount).map(o => o.valueAlias))[0];
                const firstValueSegments = firstValue.split('-');
                const firstDate = firstValueSegments.length === 2 ?
                    { year: parseInt(firstValueSegments[0]), month: parseInt(firstValueSegments[1]) } :
                    { year: 0, month: 0 };

                const lastValue = this.sortYearMonth(facet.options.filter(o => o.filteredAmount).map(o => o.valueAlias)).pop();
                const lastValueSegments = lastValue.split('-');
                const lastDate = lastValueSegments.length === 2 ?
                    { year: parseInt(lastValueSegments[0]), month: parseInt(lastValueSegments[1]) } :
                    { year: 0, month: 0 };

                return { firstDate: firstDate, lastDate: lastDate };
            },
            dates() {
                if (!this.firstAndLastDate.firstDate.year || !this.firstAndLastDate.lastDate.year) return [];
                const firstDate = this.firstAndLastDate.firstDate;
                const lastDate = this.firstAndLastDate.lastDate;
                const dates = [];
                let date = structuredClone(firstDate);
                if (firstDate.year === lastDate.year && firstDate.month === lastDate.month) {
                    dates.push({ year: date.year, month: date.month, label: `${date.year}-${date.month}` });
                    return dates;
                }
                
                let currentYear = firstDate.year;
                let currentMonth = firstDate.month;

                while (currentYear < lastDate.year || (currentYear === lastDate.year && currentMonth <= lastDate.month)) {
                    dates.push({ year: currentYear, month: currentMonth, label: `${currentYear}-${currentMonth}` });
                    currentMonth++;
                    if (currentMonth > 12) {
                        currentMonth = 1;
                        currentYear++;
                    }
                }

                return dates;
            },
            filterItems() {
                const filterItems = [{
                    Operator: 'NotEq',
                    Key: 'dossier_soort_ogp_code',
                    Values: ['BWK'],
                }];
                if (this.filters.length) {
                    this.filters.forEach(filter => {
                        if (!filter || !filter.type) return;
                        const filterItem = this.transformFilterItem(filter);
                        if (!filterItem) return;
                        filterItems.push(filterItem);
                    });
                
                }
                return filterItems;
            }
        },
        methods: {
            async onFilter(payload) {
                if (!payload) return;
                this.filters = this.filters.filter(filter => filter.type !== payload.type);
                if (typeof payload.value === 'number' || typeof payload.value === 'string') {
                    if (payload.value) this.filters.push(payload);
                }
                else {
                    if (payload.value.length) this.filters.push(payload);
                }
                await this.fetchData();
                this.$emit('filter');
            },
            async fetchData() {
                try {
                    const storeDossiers = this.$store.getters.getDossiersTransferred;
                    if (!storeDossiers.length) {
                        const filterItems = this.filterItems;
                        let offset = 0;
                        const limit = 0;

                        const response = await axios.post(
                            this.api,
                            {
                                Limit: limit,
                                Offset: offset,
                                Text: '',
                                FilterItems: filterItems,
                            }
                        );
                        if (!response || response.status != 200 || !response.data) throw 'no response or bad status';
                        this.data = response.data;
                    }
                } catch (error) {
                    console.error('Error loading JSON data: ', error);
                }
            }
        },
        async created() {
            this.loading = true;
            await this.fetchData();
            this.loading = false;
        },
    };
</script>

<template>
    <div v-if="loading" class="has-text-centered">
        <div class="lds-facebook has-text-grey-lighter">
            <div></div>
            <div></div>
            <div></div>
        </div>
    </div>
    <template v-else>
        <template v-if="facetsSet && data.totalCount">
            <div class="tile is-parent">
                <div class="tile is-3 is-parent is-vertical">
                    <div class="tile is-child">
                        <widget-text :label="$store.getters.getText('dashboardFilterKindDebit')" />
                    </div>
                    <div class="tile is-child">
                        <filter-waiting-status :data="data" @filter="(payload) => onFilter(payload)" :title="$store.getters.getText('dashboardFilterWaitingstatus')" />
                    </div>
                </div>
                <div class="tile is-9 is-parent is-vertical">
                    <div class="tile is-parent">
                        <div class="tile is-child">
                            <filter-client-name :data="data" @filter="(payload) => onFilter(payload)" />
                        </div>
                        <div class="tile is-child">
                            <filter-client-id :data="data" @filter="(payload) => onFilter(payload)" />
                        </div>
                        <div class="tile is-child">
                            <filter-creditor-name :data="data" @filter="(payload) => onFilter(payload)" />
                        </div>
                        <div class="tile is-child">
                            <filter-creditor-id :data="data" @filter="(payload) => onFilter(payload)" />
                        </div>
                    </div>
                    <div class="tile is-parent">
                        <div class="tile is-child">
                            <filter-period :data="data"
                                           :periods="periods"
                                           :most-recent-year="mostRecentYear"
                                           :second-recent-year="secondRecentYear"
                                           :is-closed="false"
                                           @filter="(payload) => onFilter(payload)" />
                        </div>
                        <div class="tile is-child">
                            <filter-phase :data="data" @filter="(payload) => onFilter(payload)" />
                        </div>
                        <div class="tile is-child">
                            <filter-closing-reason :data="data" @filter="(payload) => onFilter(payload)" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="tile is-parent">
                <div class="tile is-child is-3">
                    <widget-average-efficiency-per-period :data="data" 
                                                          :periods="periods"
                                                          :most-recent-year="mostRecentYear"
                                                          :second-recent-year="secondRecentYear"
                                                          :is-closed="false" />
                </div>
                <div class="tile is-9 is-parent is-vertical">
                    <div class="tile is-parent">
                        <div class="tile is-child is-8">
                            <widget-dossiers-per-month :data="data" :dates="dates" :is-closed="false" />
                        </div>
                        <div class="tile is-4 is-parent is-vertical">
                            <div class="tile is-child is-flex-grow-0">
                                <widget-dossiers-in-treatment :data="data" />
                            </div>
                            <div class="tile is-child is-flex-grow-0">
                                <widget-dossiers-closed :data="data" />
                            </div>
                            <div class="tile is-child is-flex-shrink-0">
                                <widget-dossiers-sums :data="data" />
                            </div>
                        </div>
                    </div>
                    <div class="tile is-parent">
                        <div class="tile is-child is-8">
                            <widget-average-efficiency-per-month :data="data" :dates="dates" :is-closed="false" />
                        </div>
                        <div class="tile is-child is-4">
                            <widget-dossiers-per-status-per-period :data="data" 
                                                                   :periods="periods"
                                                                   :most-recent-year="mostRecentYear"
                                                                   :second-recent-year="secondRecentYear" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <p class="pt-4 has-text-centered">{{ $store.getters.getText('dashboardLabelNoresultsfound') }}</p>
        </template>
    </template>
</template>